import { Button, ButtonGroup, DateTimeInput, Field, NumericInput, TextInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import { WorksheetBalanceFieldSet, WorksheetGridFieldSection, WorksheetRecordGrid } from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getDryMassGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/BsEn/gridFieldSets';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { valueOrDefault } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pipe } from 'rxjs';

let WholePortionStandardOvenDrySingleTraySection: React.FC<IWorksheetComponent> = ({
  createWorksheetSelector,
  prefixName,
}) => {
  const dispatch = useDispatch();

  const lightweightAggregate: boolean = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.LightweightAggregate', false), valueOrDefault(false))
  );

  const showTimeInOven = useSelector(
    pipe(createDryMassEntryOptionSelector(createWorksheetSelector!), (v) => v !== 'Never')
  );

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!('DryingTrays[0].DryMassReadings'),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  const trays = useSelector(createWorksheetSelector!('DryingTrays'), deepEquals) as any[];

  const updateTrays = (value: any[]) => {
    dispatch(
      actions.setValue({
        name: prefixName!('DryingTrays'),
        value: value,
        sync: true,
      })
    );
  };

  return (
    <>
      <ListFieldSection>
        <Field input={<TextInput />} name="DryingTrays[0].ContainerLabel" maxLength={20} />
        <Field input={<NumericInput />} name="DryingTrays[0].ContainerMass" />
        <FieldSet>
          <Field input={<NumericInput />} name="DryingTrays[0].WetSoilAndContainerMass" />
          <ButtonGroup>
            <Button onClick={() => updateTrays([...trays, {}])}>Add Tray</Button>
          </ButtonGroup>
        </FieldSet>
        <Field
          input={<TextOutput />}
          name="DryingTrays[0].WetSoilMass"
          additionalMessagePaths={['Measurements.TotalWetMass']}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field input={<TextOutput />} name="Measurements.Volume" visible={lightweightAggregate} />
      </ListFieldSection>
      <ListFieldSection>
        {showTimeInOven && <Field input={<DateTimeInput />} name="DryingTrays[0].DateTimePlacedInOvenLocal" />}
        <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
      </ListFieldSection>

      {showSingleReadingSection ? (
        <ListFieldSection>
          <Field
            input={<NumericInput />}
            name="DryingTrays[0].DryMassReadings[0].DrySoilAndContainerMass"
            // We do not show the dry mass calculated from the Tare + Dry mass, so we need to show its messages on this field
            additionalMessagePaths={['DryingTrays[0].DryMassReadings[0].DrySoilMass']}
            label="Tare and Material Dry Mass"
          />
        </ListFieldSection>
      ) : (
        <WorksheetGridFieldSection name="DryingTrays[0].DryMassReadings">
          <WorksheetRecordGrid
            name="DryingTrays[0].DryMassReadings"
            orientation="vertical"
            recordProps={{
              enforceAtLeastOneRecord: true,
              preventRemove: 'all-but-last',
              preventCreate: dryMassEntryOption === 'Never',
              removeConfirm: {
                confirm: true,
                confirmTitle: 'Delete Dry Mass Reading',
                confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                confirmButtonText: 'Delete',
              },
            }}
            fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
          />
        </WorksheetGridFieldSection>
      )}
      <ListFieldSection>
        <Field input={<TextOutput />} name="Measurements.TotalDryMass" label="Material Dry Mass" />
      </ListFieldSection>
    </>
  );
};

WholePortionStandardOvenDrySingleTraySection = withInjectedProps(WholePortionStandardOvenDrySingleTraySection);

export { WholePortionStandardOvenDrySingleTraySection };
export default WholePortionStandardOvenDrySingleTraySection;
